// Core
import { useState } from 'react';
import { useQuery } from 'react-query';
import { Box, Button, Flex, Text, Grid, Image, Skeleton, useColorMode, useDisclosure } from '@chakra-ui/react';

// Assets
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import pauseImage from 'assets/img/box-pause.png';
import upgradeImage from 'assets/img/box-upgrade.png';
import discountImage from 'assets/img/box-discount.png';
import glow15Image from 'assets/img/glow-15.png';
import glow16Image from 'assets/img/glow-16.png';
import { apiService } from 'services/ApiService';

// Components
import Card from 'components/Card/Card';
import Li from 'components/Lists/List';
import { StarsWhiteIcon } from 'components/Icons/Icons';
import CouponModal from './modals/CouponModal';
import ErrorCard from 'components/Form/Errors';

const ContinueSaving = ({ userData, setStep, isDark, onClose, changeStatusSubItem}) => {
	const { isOpen: isOpenCoupon, onClose: onCloseCoupon, onOpen: onOpenCoupon } = useDisclosure();

	const [errorCard, setErrorCard] = useState();
	const [btnLoading, setBtnLoading] = useState(false);
	const handleClaim = async () => {
		setBtnLoading(true);
		setErrorCard();
		try {
			const response = await apiService.getCoupon({id: changeStatusSubItem?.subscriptionId});
			if (response.statusCode === 200) {
			  onOpenCoupon();
			  setBtnLoading(false);
			} else {
			  setErrorCard(response.message);
			  setBtnLoading(false);	
			}
		} catch(e){
			setBtnLoading(false);
		}
    };

    const getOffer = () => {
    	if(changeStatusSubItem?.plan?.paymentType === 'yearly'){
    		return changeStatusSubItem?.plan?.price ? (Math.trunc(changeStatusSubItem?.plan?.price * 0.75) / 100).toFixed(2) : '';
    	} else {
			return changeStatusSubItem?.plan?.price ? (Math.trunc(changeStatusSubItem?.plan?.price / 2) / 100).toFixed(2) : '';
		}
    };
    
    return (
    	<>
    		{ userData?.isDiscountAddedToSubscription === false &&
    		<Box 
    			borderRadius="16px" 
    			textAlign="center"
    			position="relative"
    			overflow="hidden"
    			p={{base: '14px', md: '20px 20px 32px 20px'}}
    			mb={{ base: '40px', md: '64px'}}
    			bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">
    			<Box position="relative" zIndex="2">
	    			<Text
	    				fontSize="24px"
						fontStyle="italic"
						fontWeight="900"
						lineHeight="normal"
						textTransform="uppercase" 
						color="#FFF"
	    				>Final Offer</Text>

	    			<Text
	    				maxW="520px"
	    				w="100%"
	    				fontSize={{ base: '32px', md: '48px'}}
						fontStyle="normal"
						fontWeight="900"
						lineHeight="100%"
						letterSpacing="-1px"
						color="white"
						textTransform="uppercase"
						borderRadius="16px"
						m="16px auto 8px auto"
						p={{base: '6px 0px', sm: '4px 0'}}
						border="2px solid #FFF"
						bg="linear-gradient(180deg, #26BD47 7.14%, #0D6220 136.61%), #FFF"
	    				>
	    				{ changeStatusSubItem?.plan?.paymentType === 'yearly' ? '25' : '50'}% OFF { changeStatusSubItem?.plan?.paymentType === 'yearly' ? '12' : '3'} MONTHS
	    			</Text>

	    			<Text variant="header" fontSize={{ base: '24px', md: '48px'}} fontStyle="normal" color="white">Loyalty Membership</Text>

	    			<Text 
	    				fontSize="24px"
						fontStyle="italic"
						fontWeight="800"
						mt="10px"
						lineHeight="normal"
						letterSpacing="-1px"
	    				textDecorationLine="line-through"
	    				color="rgba(255, 255, 255, 0.80)">
	    				Was ${changeStatusSubItem?.plan?.price / 100}</Text>

	    			<Flex gap="8px" justifyContent="center" mt="8px" alignItems="center">
						<StarsWhiteIcon fontSize="24px" overflow="visible" />
						<Box bg="white" borderRadius="16px" padding="2px 9px 2px 12px">
							<Text variant="header-gradient" fontSize={{base: '20px', md: '24px'}} pr="3px" color="white">
								Now Only ${getOffer()} a {changeStatusSubItem?.plan?.paymentType === 'yearly' ? 'Year' : 'Month'}</Text>
						</Box>
						<StarsWhiteIcon fontSize="24px" overflow="visible" />
					</Flex>

					<Box as={Button}
						maxW="346px"
						w="100%"
						m="20px auto 0 auto"
						borderRadius="16px"
						border="2px solid #FFF"
						h="56px"
						cursor="pointer"
						isLoading={btnLoading}
						onClick={handleClaim}
						bg="linear-gradient(180deg, #26BD47 7.14%, #0D6220 136.61%)"
						boxShadow="0 1px 22.4px 0 rgba(255, 255, 255, 0.60)">
						<Text variant="title-md" p="16px 32px">Claim { changeStatusSubItem?.plan?.paymentType === 'yearly' ? '25' : '50'}% off { changeStatusSubItem?.plan?.paymentType === 'yearly' ? '12' : '3'} months</Text>
					</Box>

					{ errorCard && ( 
						<ErrorCard 
							mt={{base: '10px'}} 
							error={errorCard} /> )}
				</Box>

				<Image zIndex="0" src={glow15Image} alt="Final Offer" position="absolute" 
					bottom={{ base: '-50%', sm: '-80%', md: '-70%', lg: '-60%'}} 
					left={{ base: '-30%', sm: '-30%', md: '-26%', lg: '-20%'}} 
					/>
				<Image zIndex="0" src={glow16Image} alt="Final Offer" position="absolute" 
					bottom={{ base: '-50%', sm: '-80%', md: '-70%', lg: '-60%'}}
					right={{ base: '-30%', sm: '-30%', md: '-26%', lg: '-20%'}} 
					/>

				<CouponModal
					onOpen={onOpenCoupon}
					isOpen={isOpenCoupon}
					onClose={onCloseCoupon}
					handleOnClose={onClose}
					setStep={setStep}
					/>
    		</Box>
    		}

	        <Box>
		        <Text textAlign="center" variant="header" fontSize={{base: '36px', md: '44px', lg: '56px'}}>
		          Are You Sure You
		          <br /> Want to<Text as="span" variant="header-gradient" fontSize={{base: '36px', md: '44px', lg: '56px'}}>Miss Out?</Text>
		        </Text>

		        <Text 
			        textAlign="center" 
			        maxW="710px" 
		        	m={{ base: '10px auto 24px auto', md: '16px auto 32px auto', lg: '20px auto 40px auto'}} 
		        	variant="header" 
		        	fontSize="24px">
		          Chose one of the following below to continue saving
		        </Text>
	        </Box>

	        <Grid gap="24px" w="100%" templateColumns={{ sm: '1fr', md: '1fr 1fr' }}>
	        	<Card padding="0px" overflow="hidden" >
		        	<Grid templateColumns={{ sm: '1fr', md: '1fr 0.8fr' }} gap="0px" templateRows='1fr' position="relative">
		        		<Box position="relative" zIndex="8" h={{base: '390px'}}>
		        			<Box p="21px 16px 16px 16px" zIndex="2" position="relative">
			        			<Text variant="header" lineHeight="121%" fontStyle="normal" fontSize={{base: '36px', sm: '44px'}}>
			        				Pause Subscription
			        			</Text>

			        			<Text 
			        				variant="text-md"
			        				maxW={{base: '100%', md: '82%'}} 
			        				mt="10px">Pause Subscription for 1 Month</Text>

			        			<Text
			        				mt="10px"
			        				borderRadius="12px"
			        				color="#FFF"
			        				fontSize="24px"
									fontStyle="italic"
									fontWeight="900"
									w="265px"
									textAlign="center"
									lineHeight="normal"
									bg="linear-gradient(180deg, #26BD47 7.14%, #0D6220 136.61%), #FFF"
			        				>
			        				SAVE YOUR ENTRIES
			        			</Text>

			        			<Text 
			        				variant="text-md"
			        				maxW={{base: '100%', md: '82%'}} 
			        				mt="10px">
			        				and automatically rejoin.</Text>
		        			</Box>
		        			{ isDark ? (
		        				<Lines className="abstract-lines" style={{ zIndex: 1, top: '-169px', transform: 'rotate(-36deg) scale(0.8)', left: '-41%' }} />
		        			) : (
		        				<LinesWhite className="abstract-lines" style={{ zIndex: 1, top: '-169px', transform: 'rotate(-36deg) scale(0.8)', left: '-41%', opacity: 0.3 }} />
		        			)}
		        		</Box>

		        		<Box h={{base: '270px', md: 'unset'}} zIndex="7" position="absolute" w="100%" h="100%">
		        			{ isDark && <Box bg="#099BFF" zIndex="0" filter="blur(100px)" borderRadius="100%" m="0 auto" w="35%" right="10px" 
	        					top={{ base: '100px', md: '0px'}} 
	        					h="270px" position="absolute" />}
		        			<Image 
		        				src={pauseImage} 
		        				objectFit="cover"
		        				overflow="visible"
		        				position="absolute"
		        				w={{ base: '300px', sm: '460px'}}
		        				h="100%"
		        				alt="Pause" 
		        				right={{ base: '-34px', sm: '-64px'}}
    							bottom={{ base: '-80px', sm: '-80px'}}
		        				zIndex="2" />
		        		</Box>
		        	</Grid>
		        </Card>

		        <Card padding="0px" overflow="hidden">
		        	<Grid templateColumns={{ sm: '1fr', md: '1fr 0.8fr' }} gap="0px" templateRows='1fr' position="relative">
		        		<Box position="relative" zIndex="9" h={{base: '390px'}}>
		        			<Box p="21px 16px 16px 16px" zIndex="2" position="relative">
			        			<Text variant="header" lineHeight="121%" 
			        				maxW={{base: '100%'}} 
			        				fontStyle="normal" fontSize={{base: '36px', sm: '48px'}}>
			        				Downgrade Subscription
			        			</Text>

			        			<Text 
			        				zIndex="5"
			        				variant="text-md"
			        				position="relative"
			        				maxW={{base: '50%', md: '70%'}} 
			        				mt="10px">Lower your membership to a different tier so you can still be in giveaways and access discounts.</Text>
		        			</Box>
		        			{ isDark ? (
		        				<Lines className="abstract-lines" style={{ zIndex: 1, top: '-169px', transform: 'rotate(-36deg) scale(0.8)', left: '-41%' }} />
		        			) : (
		        				<LinesWhite className="abstract-lines" style={{ zIndex: 1, top: '-169px', transform: 'rotate(-36deg) scale(0.8)', left: '-41%', opacity: 0.3 }} />
		        			)}
		        		</Box>

		        		<Box h={{base: '270px', md: 'unset'}} zIndex="7" position="absolute" w="100%" h="100%">
		        			{ isDark && <Box bg="#099BFF" zIndex="0" filter="blur(100px)" borderRadius="100%" m="0 auto" w="35%" right="10px" 
		        			top={{ base: '100px', md: '0px'}} 
		        			h="270px" position="absolute" />}
		        			<Image src={upgradeImage} 
		        				objectFit="cover"
		        				overflow="visible"
		        				position="absolute"
		        				w="550px"
		        				h="100%"
		        				alt="Upgrade" 
		        				zIndex="2"
		        				right="-114px"
    							bottom="-80px"
    							transform={{ base: 'scale(1.2)', md: 'scale(1.03)'}}
		        				zIndex="1" />
		        		</Box>
		        	</Grid>
		        </Card>
	        </Grid>

	        <Card mt="24px" padding="0px" overflow="hidden" >
		        <Grid templateColumns={{ sm: '1fr', md: '1fr 0.3fr' }} gap="0px" templateRows='1fr'>
	        		<Box position="relative" zIndex="8">
	        			<Box 
	        				p={{ base: '16px 16px 230px 16px', md: '16px' }}
	        				zIndex="2" 
	        				position="relative">
		        			<Text variant="header" fontStyle="normal" fontSize={{base: '36px', sm: '44px', lg: '56px'}}>
		        				You’ll Miss Out On...
		        			</Text>

		        			<Box maxW={{base: '100%', 'md': '60%'}} pb="12px">
			        			<Li text="Already given away over <span style='color:#099BFF;font-weight:700'>$120,000,000+</span> to over 1000+ Winners Across Australia" />
			        			<Li text="Automatic entry to win <span style='color:#099BFF;font-weight:700'>$1,000,000</span> every month!" />
			        			<Li text="<span style='color:#099BFF;font-weight:700'>$1000s</span>, saved through exclusive partner discounts." />
			        			<Li text="Access to <span style='color:#099BFF;font-weight:700'>LMCT+ Partner Marketplace</span>" />
			        			<Li text="Access to <span style='color:#099BFF;font-weight:700'>2,000+</span> exclusive partner discounts" />
			        			<Li text="Access to <span style='color:#099BFF;font-weight:700'>LMCT+ VIP Events</span> Australia Wide" />
		        			</Box>
	        			</Box>
	        			{ isDark ? (
	        				<Lines className="abstract-lines" style={{ zIndex: 1, top: '-121px', transform: 'rotate(-36deg) scale(1)', left: '60%' }} />
	        			) : (
	        				<LinesWhite className="abstract-lines" style={{ zIndex: 1, top: '-121px', transform: 'rotate(-36deg) scale(1)', left: '60%', opacity: 0.3 }} />
	        			)}
	        		</Box>

	        		<Box h="auto" zIndex="7" position="absolute" w="100%" h="100%">
	        			{ isDark && <Box bg="#099BFF" zIndex="2" filter="blur(120px)" borderRadius="100%" m="0 auto" w="35%" right="10px" 
	        				top={{ base: '60%', md: '70px'}} 
	        				h="270px" position="absolute" />}
	        			<Image src={discountImage} 
	        				objectFit="contain"
	        				overflow="visible"
	        				position="absolute"
	        				maxW={{ base: '576px', sm: '476px', md: '580px', lg: '690px'}}
	        				w="100%"
	        				h="auto"
	        				alt="Upgrade" 
	        				right="-80px"
							bottom={{ base: '-60px', sm: '-150px', md: '-190px'}}
	        				zIndex="1" />
	        		</Box>
	        	</Grid>
        	</Card>
	    </>
    );
};


export default ContinueSaving;